import {
  Alert,
  Box,
  Button,
  Divider,
  Group,
  Modal,
  NumberInput,
  PasswordInput,
  SegmentedControl,
  Stack,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core"
import { useForm } from "@mantine/form"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { IInboundOrderItem } from "../../apis/inbound-order"
import { formatQuantity } from "../../utils/format-quantity"
import { translateBackendError } from "../delivery/translate-error"
import { useInboundMutations } from "./hooks"
import { inboundItemLabel } from "./inbound-item-label"

interface ReceiveItemForm {
  inboundOrderItemId: string
  receivedQuantity: number
  condition: string
  photoUrlsInput: string
  notes: string
}

interface Props {
  opened: boolean
  onClose: () => void
  inboundOrderId: string | null
  items: IInboundOrderItem[]
}

export function ReceiveDialog({ opened, onClose, inboundOrderId, items }: Props) {
  const { t } = useTranslation("inbound")
  const CONDITION_OPTIONS = [
    { label: t("condition.GOOD"), value: "GOOD" },
    { label: t("condition.DAMAGED"), value: "DAMAGED" },
    { label: t("condition.MIXED"), value: "MIXED" },
  ]
  const mutations = useInboundMutations()
  // RC-A — set when receive succeeded but the нөöц increment failed; keeps the
  // dialog open and offers the idempotent repost retry.
  const [stockFailed, setStockFailed] = useState<{ stockError?: string } | null>(null)

  const form = useForm<{
    driverPin: string
    receiverNotes: string
    receiveItems: ReceiveItemForm[]
  }>({
    initialValues: {
      driverPin: "",
      receiverNotes: "",
      receiveItems: items.map((item) => ({
        inboundOrderItemId: item.id,
        receivedQuantity: item.dispatchedQuantity ?? item.requestedQuantity,
        condition: "GOOD",
        photoUrlsInput: "",
        notes: "",
      })),
    },
    validate: {
      driverPin: (v) => (/^\d{6}$/.test(v) ? null : t("receive.driverPinRequired")),
    },
  })

  const handleSubmit = async (values: typeof form.values) => {
    if (!inboundOrderId) return
    const assembled = values.receiveItems.map((ri) => ({
      inboundOrderItemId: ri.inboundOrderItemId,
      receivedQuantity: ri.receivedQuantity,
      condition: ri.condition,
      photoUrls: ri.photoUrlsInput
        ? ri.photoUrlsInput
            .split(",")
            .map((u) => u.trim())
            .filter(Boolean)
        : undefined,
      notes: ri.notes || undefined,
    }))

    try {
      const res = await mutations.receive(inboundOrderId, {
        driverPin: values.driverPin,
        receiverNotes: values.receiverNotes || undefined,
        items: assembled,
      })
      if (res && res.stockPosted === false) {
        // Order is RECEIVED but stock did not move — surface + offer retry.
        setStockFailed({ stockError: res.stockError })
        return
      }
      setStockFailed(null)
      form.reset()
      onClose()
    } catch {
      // error surfaced via mutations.error
    }
  }

  const handleRepost = async () => {
    if (!inboundOrderId) return
    try {
      const r = await mutations.repostStock(inboundOrderId)
      if (r.stockPosted) {
        setStockFailed(null)
        form.reset()
        onClose()
      } else {
        setStockFailed({ stockError: r.stockError })
      }
    } catch {
      // error surfaced via mutations.error
    }
  }

  const closeAndReset = () => {
    setStockFailed(null)
    onClose()
  }

  return (
    <Modal opened={opened} onClose={closeAndReset} title={t("receive.title")} size="xl">
      <Box component="form" onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="sm">
          <PasswordInput
            label={t("receive.driverPin")}
            placeholder={t("receive.driverPinPlaceholder")}
            required
            inputMode="numeric"
            maxLength={6}
            {...form.getInputProps("driverPin")}
          />

          <Textarea
            label={t("receive.receiverNotes")}
            placeholder={t("receive.receiverNotesPlaceholder")}
            rows={2}
            {...form.getInputProps("receiverNotes")}
          />

          <Divider label={t("receive.itemsList")} labelPosition="center" />

          {items.map((item, index) => (
            <Box key={item.id} p="sm" style={{ border: "1px solid var(--mantine-color-gray-3)", borderRadius: "var(--mantine-radius-md)" }}>
              <Text fz={14} fw={600} mb="xs">
                {inboundItemLabel(item, t)}
                {item.unit ? ` (${item.unit})` : ""}
                {` — ${t("receive.requested")}: `}
                <Text span fz={14} c="gray.8">{formatQuantity(item.requestedQuantity)}</Text>
              </Text>

              <Group gap="sm" align="flex-end">
                <NumberInput
                  label={t("receive.receivedQuantity")}
                  min={0}
                  w={140}
                  {...form.getInputProps(`receiveItems.${index}.receivedQuantity`)}
                />
                <Stack gap={4}>
                  <Text fz={14} fw={500}>{t("receive.condition")}</Text>
                  <SegmentedControl
                    data={CONDITION_OPTIONS}
                    size="sm"
                    {...form.getInputProps(`receiveItems.${index}.condition`)}
                  />
                </Stack>
              </Group>

              <TextInput
                mt="xs"
                label={t("receive.photoUrls")}
                placeholder="https://..., https://..."
                size="sm"
                {...form.getInputProps(`receiveItems.${index}.photoUrlsInput`)}
              />
              <TextInput
                mt="xs"
                label={t("receive.notes")}
                placeholder={t("receive.notesPlaceholder")}
                size="sm"
                {...form.getInputProps(`receiveItems.${index}.notes`)}
              />
            </Box>
          ))}

          {items.length === 0 && (
            <Text c="gray.8" ta="center" fz={14}>
              {t("receive.noItems")}
            </Text>
          )}

          {mutations.error && (
            <Text fz={14} fw={600}>
              {/* Backend keys (error.custody.bad_pin, error.pin.locked) read from the errors namespace. */}
              {translateBackendError(mutations.error)}
            </Text>
          )}

          {stockFailed && (
            <Alert color="yellow" variant="light" title={t("receive.stockFailedTitle")}>
              <Stack gap="xs">
                <Text fz={14}>
                  {t("receive.stockFailedBody")}
                </Text>
                {stockFailed.stockError && (
                  <Text fz={14} c="gray.8">
                    {stockFailed.stockError}
                  </Text>
                )}
                <Group justify="flex-end">
                  <Button
                    size="sm"
                    variant="default"
                    loading={mutations.submitting}
                    onClick={handleRepost}>
                    {t("receive.retry")}
                  </Button>
                </Group>
              </Stack>
            </Alert>
          )}

          <Group justify="flex-end" mt="md">
            {stockFailed ? (
              <Button variant="subtle" onClick={closeAndReset}>
                {t("receive.close")}
              </Button>
            ) : (
              <>
                <Button variant="subtle" onClick={closeAndReset}>
                  {t("receive.cancel")}
                </Button>
                <Button type="submit" loading={mutations.submitting} color="green">
                  {t("receive.submit")}
                </Button>
              </>
            )}
          </Group>
        </Stack>
      </Box>
    </Modal>
  )
}
